import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ChevronRight, Star, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import OrderDetailModal from '../../components/OrderDetailModal';
import ProductReviewModal from '../../components/ProductReviewModal';
import { useAuth } from '../../context/AuthContext';

const OrdersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [orderToReview, setOrderToReview] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchOrders();
  }, [page]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE}/api/orders/user?page=${page}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
        setPages(data.pages || 1);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-gray-100 text-gray-800',
      shipped: 'bg-red-100 text-red-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading && orders.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12">
        <div className="container mx-auto px-4">
          <p className="text-center">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back Button - Mobile Only */}
        {user && (user.role === 'trainer' || user.role === 'user') && (
          <Button
            onClick={() => navigate(`/${user.role}/dashboard?tab=orders`)}
            variant="ghost"
            className="mb-4 sm:hidden flex items-center gap-2 text-red-600 hover:text-red-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
        )}
        
        <h1 className="text-4xl font-bold mb-8 bg-gradient-to-r from-red-600 to-black bg-clip-text text-transparent">
          My Orders
        </h1>

        {orders.length === 0 ? (
          <Card className="dark:bg-gray-800 dark:border-gray-700">
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h2 className="text-2xl font-bold mb-2">No orders yet</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Start shopping to see your orders here!</p>
              <Link to="/shop">
                <Button>Go to Shop</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Order #{order.order_number}</CardTitle>
                      <p className="text-sm text-gray-600">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <Badge className={getStatusColor(order.order_status)}>
                      {order.order_status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total</span>
                      <span className="font-bold text-lg">${order.total.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Items</span>
                      <span>{order.items.length} item(s)</span>
                    </div>
                    
                    {order.tracking_number && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Tracking</span>
                        <span className="font-mono">{order.tracking_number}</span>
                      </div>
                    )}

                    <div className="pt-2 border-t">
                      <p className="text-sm font-semibold mb-1">Items:</p>
                      {order.items.slice(0, 3).map((item, idx) => (
                        <p key={item.product_id || `${item.product_title}-${idx}`} className="text-sm text-gray-600">
                          • {item.product_title} {item.variant_title && `(${item.variant_title})`} × {item.quantity}
                        </p>
                      ))}
                      {order.items.length > 3 && (
                        <p className="text-sm text-gray-500">+ {order.items.length - 3} more items</p>
                      )}
                    </div>

                    <div className="flex gap-2 mt-3">
                      <Button 
                        variant="outline" 
                        className="flex-1" 
                        size="sm"
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowOrderModal(true);
                        }}
                      >
                        View Details <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                      
                      {order.order_status === 'delivered' && (
                        <>
                          {/* Show disabled button if review was deleted by admin */}
                          {order.review_status === 'deleted' && (
                            <Button 
                              className="flex-1 bg-gray-400 cursor-not-allowed opacity-60" 
                              size="sm"
                              disabled
                              title="Your review was removed by admin. You cannot review this order again."
                            >
                              <Star className="w-4 h-4 mr-1" />
                              Review Removed
                            </Button>
                          )}
                          
                          {/* Show Write Review button if no review or review is pending/rejected */}
                          {(order.review_status === 'none' || order.review_status === 'pending' || order.review_status === 'rejected') && (
                            <Button 
                              className="flex-1 bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900" 
                              size="sm"
                              onClick={() => {
                                setOrderToReview(order);
                                setShowReviewModal(true);
                              }}
                            >
                              <Star className="w-4 h-4 mr-1" />
                              {order.review_status === 'pending' ? 'Edit Review' : order.review_status === 'rejected' ? 'Resubmit Review' : 'Write Review'}
                            </Button>
                          )}
                          
                          {/* Show badge if review is approved */}
                          {order.review_status === 'approved' && (
                            <Badge className="flex-1 bg-green-100 text-green-800 justify-center">
                              ✓ Reviewed
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-600">
                  Page {page} of {pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      <OrderDetailModal 
        order={selectedOrder}
        isOpen={showOrderModal}
        onClose={() => {
          setShowOrderModal(false);
          setSelectedOrder(null);
        }}
      />

      {/* Product Review Modal */}
      <ProductReviewModal 
        order={orderToReview}
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setOrderToReview(null);
        }}
        onSuccess={() => {
          fetchOrders();
        }}
      />
    </div>
  );
};

export default OrdersPage;
